import React, { useState, useEffect } from 'react';
import Autocomplete from '@material-ui/lab/Autocomplete';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  colors,
  Divider,
  FormControlLabel,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { ConsumableItem } from 'api/consumablesApi';
import { EquipmentCategoryItem } from 'api/equipmentApi';

interface Props {
  label: string;
  onChange: (data: ConsumableItem[] | EquipmentCategoryItem[]) => void;
  defaultSelected?: ConsumableItem[] | EquipmentCategoryItem[];
  data?: any[];
  isFetching?: boolean;
}

const MultiItemNamesAutoCompleteForFilters = ({
  label,
  onChange,
  defaultSelected,
  data,
  isFetching,
}: Props) => {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<ConsumableItem[]>([]);

  useEffect(() => {
    if (defaultSelected && defaultSelected.length > 0 && data) {
      const selectedData = data?.filter((item) =>
        defaultSelected.some(
          (selectedItem: ConsumableItem | EquipmentCategoryItem) =>
            selectedItem.id === item.id
        )
      );
      setSelected(selectedData);
    } else {
      setSelected([]);
    }
  }, [defaultSelected, data]);

  return (
    <>
      <Autocomplete
        multiple
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        options={isFetching ? [] : data}
        loading={open && isFetching}
        getOptionLabel={(option) => option.name}
        value={selected}
        renderOption={(option) => (
          <span>
            <Box display="block">
              <Typography variant="body1">{option.name}</Typography>
            </Box>
          </span>
        )}
        renderTags={(value: ConsumableItem[], getTagProps) =>
          value.map((option: ConsumableItem, index: number) => (
            <Chip
              sx={{ marginRight: '0.4rem' }}
              variant="outlined"
              label={option.name}
              {...getTagProps({ index })}
            />
          ))
        }
        getOptionSelected={(option, value) => option.id === value.id}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            variant="outlined"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {open && isFetching && (
                    <CircularProgress color="inherit" size={20} />
                  )}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        onChange={(_, data: ConsumableItem[]) => {
          onChange && onChange(data.map((u) => u));
        }}
        PaperComponent={(paperProps) => {
          const { children, ...restPaperProps } = paperProps;
          return (
            <Paper {...restPaperProps}>
              {data?.length > 0 && (
                <Box onMouseDown={(e) => e.preventDefault()} pl={1.5} py={0.5}>
                  <FormControlLabel
                    onClick={(e) => {
                      e.preventDefault();
                      onChange && onChange(data?.map((u) => u));
                      setOpen(false);
                    }}
                    label=""
                    control={
                      <Box m={1}>
                        <Button
                          id="select-all-checkbox"
                          variant="outlined"
                          size="small"
                          sx={{
                            color: colors.grey[600],
                            borderColor: colors.grey[600],
                          }}>
                          Select all
                        </Button>
                      </Box>
                    }
                  />
                </Box>
              )}
              <Divider />
              {children}
            </Paper>
          );
        }}
      />
    </>
  );
};

export default MultiItemNamesAutoCompleteForFilters;
